import * as i0 from '@angular/core';
import { Injectable, Component, Input, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Subject } from 'rxjs';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Spinner Keys
 */
const _c0 = a0 => ({
  "background-color": a0
});
function SpinnerComponent_div_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelement(1, "gds-loading", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(4, _c0, ctx_r0.backgroundColor));
    i0.ɵɵadvance();
    i0.ɵɵproperty("data", ctx_r0.data)("isInline", ctx_r0.isInline)("theme", ctx_r0.theme);
  }
}
const SPINNER_KEYS = {
  OVERRIDE: 'override',
  SUPPRESS: 'suppress',
  QUEUE: 'queue_start'
};
class SpinnerService {
  constructor() {
    this.spinner$ = new Subject();
    this.queueList = [];
  }
  /**
   * Show spinner. If waitFor is given and not set already, save value.
   * When provided, spinner will only hide if value is the same as the saved value.
   * Ignore showing if suppression is on.
   * @param  {string} [waitFor] will take any string value. IE: A good value can be the function name
   * @example
   * // USE 1:
   * this.spinner.show();
   * // USE 2: Better to use a custom value for more control
   * this.spinner.show('function_name');
   *
   * // In a later spot / different file.
   * // USE 1: Simply will hide spinner if no waitFor value is present.
   * this.spinner.hide();
   * // USE 2: Again use this way for better control
   * this.spinner.hide('function_name')
   */
  show(waitFor) {
    if (waitFor && !this.waitFor) {
      this.waitFor = waitFor;
    }
    if (this.waitFor === SPINNER_KEYS.SUPPRESS) {
      return;
    }
    this.spinner$.next(true);
  }
  /**
   * Use when you need the spinner between an unknown amount of calls and wait times. IE: You run the queue function
   * before an http call, which then spawns multiple components from the data, and those components spawn additional http calls.
   *
   * You have no way of knowing when those calls are going to complete, as it ranges between 2-x http calls, and
   * no way to attach to them with something like a Promise.all() to then hide the spinner.
   *
   * This helps situations where you don't want the spinner showing and hiding over and over in succession.
   *
   * @param {string} [key=SPINNER_KEYS.QUEUE] defaults to value of SPINNER_KEYS.QUEUE, but can use any string
   * @param {string} [parent_key] value should be tied to the first call to queue instance. IE: value of key
   * @example
   * // USE 1:
   * this.spinner.queue();
   * // USE 2: Better to use a custom value for more control
   * this.spinner.queue('parent_token');
   * // USE 3: A self starting queue, if the parent_key is missing from queue list
   * this.spinner.queue('child_of_parent_token', 'parent_token');
   *
   * // In a later spot / different file.
   * // USE 1: We have to use the original QUEUE value
   * this.spinner.queue('child_of_parent_token', SPINNER_KEYS.QUEUE);
   * // USE 2: Again use this way for better control
   * this.spinner.queue('child_of_parent_token', 'parent_token');
   *
   * // Once child_of_parent_token task is complete, run hide like normal.
   * // This covers both uses.
   * this.spinner.hide('child_of_parent_token')
   */
  queue(key = SPINNER_KEYS.QUEUE, parent_key) {
    if (this.waitFor && this.queueList.length === 0) return;
    if (!this.waitFor && !parent_key) {
      this.waitFor = key;
    }
    const find = this.queueList.find(queue => queue.key === key);
    if (find) {
      return;
    }
    const parent_item = this.queueList.find(queue => queue.key === parent_key);
    if (!parent_item && parent_key) {
      // Self starting queue without need to call empty parent_key version
      this.queueList.push({
        key: parent_key,
        parent_key: undefined
      });
    }
    this.queueList.push({
      key,
      parent_key
    });
    this.spinner$.next(true);
  }
  /**
   * Use value passed to determine if it's possible to hide, if no value was previously passed through show function, then just hide. Allow override value
   * to immediately hide spinner. If queueList present, it takes priority.
   * @param  {(string)} [waitFor] use previous value used with show, or queue function. If SPINNER_KEYS.OVERRIDE value passed, it will always hide spinner.
   * @example
   * // USE 1:
   * this.spinner.show();
   * // USE 2: Better to use a custom value for more control
   * this.spinner.show('function_name');
   *
   * // In a later spot / different file.
   * // USE 1: Simply will hide spinner if no waitFor value is present.
   * this.spinner.hide();
   * // USE 2: Again use this way for better control
   * this.spinner.hide('function_name')
   */
  hide(waitFor) {
    const queueLength = this.queueList.length;
    if (waitFor === SPINNER_KEYS.OVERRIDE || waitFor === this.waitFor && !queueLength || !waitFor && !this.waitFor && !queueLength) {
      this.clearValues();
      return;
    }
    if (queueLength && waitFor) {
      const list = this.getQueueList(waitFor);
      // let's remove current key
      list.current = list.current.filter(queue => queue.key !== waitFor);
      if (list.current.length <= 1 && list.left.length === 0) {
        // If all done, then clear all.
        this.clearValues();
        return;
      } else if (list.current.length <= 1 && list.left.length > 0) {
        // if first queue list is done but another queue list is left, then
        this.queueList = list.left;
      } else {
        this.queueList = [...list.current, ...list.left];
      }
    }
  }
  getQueueList(key) {
    const current = [];
    const left = [];
    const find = this.queueList.find(queue => queue.key === key);
    this.queueList.forEach(queue => {
      if (queue.parent_key === find?.parent_key || queue.key === key || queue.key === find?.parent_key) {
        current.push(queue);
      } else {
        left.push(queue);
      }
    });
    return {
      current,
      left
    };
  }
  /**
   * Clear spinner values for next use
   */
  clearValues() {
    this.waitFor = '';
    this.queueList = [];
    this.spinner$.next(false);
  }
  static {
    this.ɵfac = function SpinnerService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SpinnerService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SpinnerService,
      factory: SpinnerService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SpinnerService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class SpinnerComponent {
  constructor(spinner, changeDetection) {
    this.spinner = spinner;
    this.changeDetection = changeDetection;
    // Optional input to keep the loader up while it is being worked on
    this.debug = false;
    this.backgroundColor = 'rgba(0,0,0,0.6)';
    this.show = false;
  }
  ngOnInit() {
    this.spinnerSub = this.spinner.spinner$.subscribe(state => {
      if (state !== this.show) {
        this.changeDetection.detach();
        this.show = state;
        this.changeDetection.detectChanges();
        this.changeDetection.reattach();
      }
    });
  }
  ngOnDestroy() {
    this.spinnerSub && this.spinnerSub.unsubscribe();
  }
  static {
    this.ɵfac = function SpinnerComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SpinnerComponent)(i0.ɵɵdirectiveInject(SpinnerService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: SpinnerComponent,
      selectors: [["upr-spinner"]],
      inputs: {
        data: "data",
        isInline: "isInline",
        theme: "theme",
        debug: "debug"
      },
      standalone: false,
      decls: 1,
      vars: 1,
      consts: [["class", "loading", "role", "status", "aria-busy", "true", "id", "mySpin", 3, "ngStyle", 4, "ngIf"], ["role", "status", "aria-busy", "true", "id", "mySpin", 1, "loading", 3, "ngStyle"], [1, "loading-gds", 3, "data", "isInline", "theme"]],
      template: function SpinnerComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵtemplate(0, SpinnerComponent_div_0_Template, 2, 6, "div", 0);
        }
        if (rf & 2) {
          i0.ɵɵproperty("ngIf", ctx.show || ctx.debug);
        }
      },
      dependencies: [i2.NgIf, i2.NgStyle],
      styles: [".hide-spin[_ngcontent-%COMP%]{display:block}.loading[_ngcontent-%COMP%]{display:flex;height:100%;width:100%;justify-content:center;align-items:center;position:fixed;top:0;left:0;z-index:999}.loading-gds[_ngcontent-%COMP%]{margin:auto}"]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SpinnerComponent, [{
    type: Component,
    args: [{
      selector: 'upr-spinner',
      standalone: false,
      template: "<div\r\n\tclass=\"loading\"\r\n\trole=\"status\"\r\n\taria-busy=\"true\"\r\n\tid=\"mySpin\"\r\n\t*ngIf=\"show || debug\"\r\n\t[ngStyle]=\"{ 'background-color': backgroundColor }\"\r\n>\r\n\t<gds-loading\r\n\t\tclass=\"loading-gds\"\r\n\t\t[data]=\"data\"\r\n\t\t[isInline]=\"isInline\"\r\n\t\t[theme]=\"theme\"\r\n\t></gds-loading>\r\n</div>\r\n",
      styles: [".hide-spin{display:block}.loading{display:flex;height:100%;width:100%;justify-content:center;align-items:center;position:fixed;top:0;left:0;z-index:999}.loading-gds{margin:auto}\n"]
    }]
  }], () => [{
    type: SpinnerService
  }, {
    type: i0.ChangeDetectorRef
  }], {
    data: [{
      type: Input
    }],
    isInline: [{
      type: Input
    }],
    theme: [{
      type: Input
    }],
    debug: [{
      type: Input
    }]
  });
})();
class SpinnerModule {
  static {
    this.ɵfac = function SpinnerModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SpinnerModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: SpinnerModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SpinnerModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule],
      declarations: [SpinnerComponent],
      exports: [SpinnerComponent],
      schemas: [CUSTOM_ELEMENTS_SCHEMA]
    }]
  }], null, null);
})();

/*
 * Public API Surface of spinner
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SPINNER_KEYS, SpinnerComponent, SpinnerModule, SpinnerService };
